import {
    CompanyProfile,
    Component,
    ComponentLaunchStatus,
    ComponentQuery,
    ComponentType,
    cleanObject,
    getComponentPayloadValidator,
    isDefaultPort,
} from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { StringHelpers } from 'helpers/StringHelpers';

const ComponentHelpers = {
    urls: {
        search: (props?: { productsQuery?: Partial<ComponentQuery>; searchText?: string }) => {
            const query = ComponentHelpers.getSearchQuery(props);

            return `/products${query}`;
        },
        overview: () => '/products',
        manage: () => '/products/manage',
        teamComponents: () => '/team-components',
        create: ({ component, company }: { component?: string; company?: CompanyProfile } = {}) => {
            const base = component ? `/products/create/${component}` : '/products/create';

            const companyIsDistributor = CompanyHelpers.isDistributor(company);

            if (company && companyIsDistributor) {
                return `${base}?distributor=${company.id}`;
            }

            if (company) {
                return `${base}?manufacturer=${company.id}`;
            }

            return base;
        },
        view: (componentId: string, slug?: string, params?: { redirectAfterSave?: string }) => {
            const redirectAfterSave = params?.redirectAfterSave;

            if (redirectAfterSave) {
                return `/products/${componentId}?redirectAfterSave=${encodeURIComponent(redirectAfterSave)}`;
            }

            return slug ? `/products/${slug}-${componentId}` : `/products/${componentId}`;
        },
        duplicate: (componentId: string) => `/products/${componentId}/duplicate`,
        generate: (componentId: string) => `/products/${componentId}/generate`,
        bulk: (companyId: string, productSeries: string) =>
            `/products/bulk/${companyId}/${encodeURIComponent(productSeries)}`,
        dcProducts: () => '/dcproducts',
    },

    getSearchQuery: (props?: { productsQuery?: Partial<ComponentQuery>; searchText?: string }) => {
        const { productsQuery, searchText } = props || {};

        const searchParams = new URLSearchParams();

        if (productsQuery) {
            searchParams.set('query', JSON.stringify(productsQuery));
        }

        if (searchText) {
            searchParams.set('searchText', searchText);
        }

        const searchString = searchParams.toString();

        return searchString ? `?${searchString}` : '';
    },

    getSearchRequest: (query: Partial<ComponentQuery>) =>
        cleanObject(
            {
                ...query,
                ports: query.ports?.filter((port) =>
                    query.catalog === 'dc' ? !isDefaultPort(port) : Object.keys(port).length > 0,
                ),
            },
            { removeEmpty: true },
        ),

    generateMetadata: (component: Pick<Component, 'name' | 'productSeries' | 'productIdentifier'>) => {
        const { productIdentifier, productSeries } = component;

        let identifier = productIdentifier || '';

        if (productIdentifier && productSeries) {
            if (productIdentifier.toLowerCase().includes(productSeries.toLowerCase())) {
                identifier = productIdentifier;
            } else {
                identifier = `${productIdentifier}, ${productSeries}`;
            }
        }

        const shortName = component.name;
        const longName = [component.name, identifier].filter(Boolean).join(' • ');
        const name = component.name === identifier ? shortName : longName;

        const slug = StringHelpers.sluggify(name);

        return {
            identifier,
            name,
            slug,
        };
    },

    componentTypeHasElectrical: (componentType?: ComponentType) => !componentType || componentType !== 'other',
    componentTypeHasPerformance: (componentType?: ComponentType) => !componentType || componentType !== 'cable',
    componentTypeHasCommunication: (componentType?: ComponentType) => !componentType || componentType !== 'cable',

    isProtectionComponent: (componentType: ComponentType) => {
        return ['breaker', 'disconnect', 'fuse'].includes(componentType);
    },

    hasPurpose: (type: ComponentType) => {
        const componentValidator = getComponentPayloadValidator(type);
        const component = componentValidator.parse({ name: '' }) as Component;

        const port = component?.electrical?.ports?.[0];

        return port && 'purpose' in port;
    },

    parseReleaseQuarter: (value?: string | null) => {
        const match = /^(\d{4})-Q([1-4])$/.exec(value ?? '');

        return match ? { year: Number(match[1]), quarter: Number(match[2]) } : null;
    },

    formatReleaseQuarter: (year: number, quarter: number) => `${year}-Q${quarter}`,

    getLaunchLabel: (component: Pick<Component, 'lifecycle'>) => {
        const lifecycle = component.lifecycle;
        const launchStatus = lifecycle?.launchStatus ?? ComponentLaunchStatus.RELEASE_DATE;

        if (launchStatus === ComponentLaunchStatus.COMING_SOON) {
            return 'Coming Soon';
        }

        if (launchStatus === ComponentLaunchStatus.QUARTER) {
            const parsed = ComponentHelpers.parseReleaseQuarter(lifecycle?.releaseQuarter);

            if (!parsed) {
                return null;
            }

            const endOfQuarter = Date.UTC(parsed.year, parsed.quarter * 3, 1);

            if (endOfQuarter <= Date.now()) {
                return null;
            }

            return `Launching Q${parsed.quarter} ${parsed.year}`;
        }

        const release = lifecycle?.release;

        if (!release) {
            return null;
        }

        const releaseDate = new Date(release);
        const releaseTime = releaseDate.getTime();

        if (Number.isNaN(releaseTime) || releaseTime <= Date.now()) {
            return null;
        }

        const year = releaseDate.getFullYear();
        const quarter = Math.floor(releaseDate.getMonth() / 3) + 1;

        return `Launching Q${quarter} ${year}`;
    },
} as const;

export { ComponentHelpers };
